import React from "react";
import {
  act,
  fireEvent,
  render,
  screen,
  waitFor,
} from "@testing-library/react";
import "@testing-library/jest-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

jest.mock("react-router-dom", () => ({
  useParams: () => ({ workspace_id: "ws-1" }),
  useNavigate: () => jest.fn(),
  useSearchParams: () => [new URLSearchParams(""), jest.fn()],
}));

// Stable identity, like the real provider's: the results effect depends on it.
const mockWorkspace = { id: "ws-1" };
jest.mock("../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({ workspace: mockWorkspace, isEditor: false }),
}));

const mockVCFilter = jest.fn();
const mockVCIndex = jest.fn();
jest.mock("../hooks", () => ({
  useBKGVCFilter: () => mockVCFilter,
  useBKGVCIndex: () => mockVCIndex,
  useBKGVCExport: () => jest.fn(),
  useBKGIngest: () => jest.fn(),
}));

// The filter rail shows from lg up; setupTests' matchMedia answers false.
jest.mock("../hooks/useWideLayout", () => ({ useWideLayout: () => true }));

// A stand-in FilterUI: one button that commits whatever values the test sets.
let mockFilterValues: any = {};
jest.mock("./SearchFilters", () => ({
  FilterUI: ({ onChange }: any) => (
    <button onClick={() => onChange(mockFilterValues)}>apply filter</button>
  ),
}));

jest.mock("../../../components/Loading", () => () => (
  <div data-testid="initial-loading" />
));
jest.mock("../components/BKGAgent", () => ({ FloatingChatWidget: () => null }));
jest.mock("../components/ExportDropdown", () => () => null);
jest.mock("../../../components/Modals/Autodb/AddVCModal", () => () => null);

// eslint-disable-next-line import/first
import VentureCapitalSearch from "./VentureCapitalSearch";

const deferred = <T,>() => {
  let resolve!: (value: T) => void;
  const promise = new Promise<T>((r) => {
    resolve = r;
  });
  return { promise, resolve };
};

const page = (...names: string[]) => ({
  items: names.map((name) => ({
    _id: `ws-1/venture_capital/${name}`,
    value: { name: { value: name } },
  })),
  total: names.length,
});

const NAME = "value.name.value";
const schema = {
  path: "value",
  type: "object",
  children: [{ path: NAME, type: "string", allowed_values: ["Acme"] }],
};
const nameIs = (name: string) => ({
  [NAME]: { path: NAME, type: "string", allowed_values: [name] },
});

const renderVC = () =>
  render(
    <QueryClientProvider
      client={
        new QueryClient({ defaultOptions: { queries: { retry: false } } })
      }
    >
      <VentureCapitalSearch />
    </QueryClientProvider>,
  );

// The rail starts collapsed, so open it before reaching the filters.
const findApplyFilter = async () => {
  fireEvent.click(await screen.findByRole("button", { name: /^Filters/ }));
  return screen.findByRole("button", { name: "apply filter" });
};

// The add-firm autocomplete preloads names from the same endpoint with
// limit 200; answer it separately so it can't consume a queued results page.
let mockResultResponses: Promise<ReturnType<typeof page>>[] = [];
const resultCalls = () =>
  mockVCIndex.mock.calls.filter(([, params]) => params.limit !== 200);

describe("VentureCapitalSearch data loading", () => {
  beforeEach(() => {
    mockFilterValues = {};
    mockResultResponses = [];
    mockVCIndex.mockImplementation((_ws: string, params: any) =>
      params.limit === 200
        ? Promise.resolve({ items: [] })
        : (mockResultResponses.shift() ?? new Promise(() => {})),
    );
  });

  it("requests results without waiting for the filter schema", async () => {
    mockVCFilter.mockReturnValue(new Promise(() => {}));
    mockResultResponses.push(Promise.resolve(page("Acme Ventures")));

    renderVC();

    expect(await screen.findByText("Acme Ventures")).toBeInTheDocument();
    expect(resultCalls()[0][1]).toMatchObject({ limit: 50, offset: 0 });
  });

  it("keeps the current results on screen while a refetch is in flight", async () => {
    mockVCFilter.mockResolvedValue(schema);
    mockResultResponses.push(Promise.resolve(page("Acme Ventures")));

    renderVC();
    expect(await screen.findByText("Acme Ventures")).toBeInTheDocument();

    const refetch = deferred<ReturnType<typeof page>>();
    mockResultResponses.push(refetch.promise);
    mockFilterValues = nameIs("Zeta");
    fireEvent.click(await findApplyFilter());

    expect(
      screen.getByRole("progressbar", { name: "Updating results" }),
    ).toBeInTheDocument();
    expect(screen.getByText("Acme Ventures")).toBeInTheDocument();
    expect(screen.queryByTestId("initial-loading")).not.toBeInTheDocument();

    await act(async () => refetch.resolve(page("Beta Capital")));

    expect(await screen.findByText("Beta Capital")).toBeInTheDocument();
    expect(screen.queryByText("Acme Ventures")).not.toBeInTheDocument();
  });

  it("ignores a response that arrives after a newer one", async () => {
    mockVCFilter.mockResolvedValue(schema);
    const first = deferred<ReturnType<typeof page>>();
    const second = deferred<ReturnType<typeof page>>();
    mockResultResponses.push(first.promise, second.promise);

    renderVC();
    const applyFilter = await findApplyFilter();
    mockFilterValues = nameIs("Zeta");
    fireEvent.click(applyFilter);
    await waitFor(() => expect(resultCalls()).toHaveLength(2));

    await act(async () => second.resolve(page("Newer")));
    expect(await screen.findByText("Newer")).toBeInTheDocument();

    await act(async () => first.resolve(page("Stale")));
    expect(screen.queryByText("Stale")).not.toBeInTheDocument();
    expect(screen.getByText("Newer")).toBeInTheDocument();
  });
});
